/**
 * Keyboard -> BlobInput. The only place in the game that touches key events.
 *
 * Two local key maps so step 2 can put both players on one keyboard without
 * changing anything else.
 */
import type { BlobInput } from '@office/sim';

/**
 * Combine input sources. A device can have both a keyboard and a touchscreen,
 * and holding a key while tapping a button should not fight itself.
 */
export function mergeInputs(...sources: readonly BlobInput[]): BlobInput {
  const merged = { left: false, right: false, jump: false, attack: false };
  for (const source of sources) {
    merged.left ||= source.left;
    merged.right ||= source.right;
    merged.jump ||= source.jump;
    merged.attack ||= source.attack;
  }
  return merged;
}

export interface KeyMap {
  left: string;
  right: string;
  jump: string;
  attack: string;
}

export const PLAYER_ONE_KEYS: KeyMap = {
  left: 'KeyA',
  right: 'KeyD',
  jump: 'KeyW',
  attack: 'Space',
};

export const PLAYER_TWO_KEYS: KeyMap = {
  left: 'ArrowLeft',
  right: 'ArrowRight',
  jump: 'ArrowUp',
  attack: 'ArrowDown',
};

export class Keyboard {
  private readonly down = new Set<string>();
  private attached = false;

  /** Keys whose browser default is suppressed while attached. */
  swallow: (code: string) => boolean = (code) => code === 'Space' || code.startsWith('Arrow');

  attach(target: EventTarget = window): () => void {
    if (this.attached) return () => undefined;
    this.attached = true;
    const onDown = (e: Event): void => {
      const ev = e as KeyboardEvent;
      // Space and the arrows scroll the page or click a focused button, which
      // is never what a player holding a key wants. Text fields keep them.
      if (this.swallow(ev.code) && !(ev.target instanceof HTMLInputElement)) ev.preventDefault();
      if (ev.repeat) return;
      this.down.add(ev.code);
    };
    const onUp = (e: Event): void => {
      this.down.delete((e as KeyboardEvent).code);
    };
    const onBlur = (): void => this.down.clear();
    target.addEventListener('keydown', onDown);
    target.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      target.removeEventListener('keydown', onDown);
      target.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
      this.attached = false;
    };
  }

  isDown(code: string): boolean {
    return this.down.has(code);
  }

  read(map: KeyMap): BlobInput {
    return {
      left: this.down.has(map.left),
      right: this.down.has(map.right),
      jump: this.down.has(map.jump),
      attack: this.down.has(map.attack),
    };
  }

  clear(): void {
    this.down.clear();
  }
}
